<template>
    <div>
        <b-card class="card-navy" header-tag="header" footer-tag="footer">
            <template #header>
                <h6 class="mb-0">
                    {{ $t('pages.client-certificate.create.title') }}
                </h6>
            </template>
            <template #footer v-if="hasCcid">
                ID: {{ ccid }}
            </template>
            <div v-if="!requested">
                <b-row>
                    <b-form-group :description="$t('pages.client-certificate.create.current_password_hint')">
                        <b-input-group :prepend="$t('pages.client-certificate.create.current_password')">
                            <b-form-input id="current_password" v-model="current_password"
                                          type="password"></b-form-input>
                        </b-input-group>
                    </b-form-group>
                </b-row>
                <b-row>
                    <b-col class="col-5">
                        <b-button class="mt-2" variant="primary" :disabled="!passwordEntered"
                                  @click="requestCertificate">
                            <b-icon-plus aria-hidden="true"/>
                            {{ $t('pages.client-certificate.create.ok') }}
                        </b-button>
                    </b-col>
                </b-row>
            </div>
            <div v-if="requested">
                <div class="info-box bg-primary" v-if="inner_loading_indicator">
                    <span class="info-box-icon"><i class="fas fa-sync-alt"></i></span>
                    <div class="info-box-content">
                        <span class="info-box-text">{{ $t('pages.client-certificate.create.started') }}</span>
                    </div>
                    <div class="overlay">
                        <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                    </div>
                </div>

                <div id="passphrase" v-if="hasPassphrase">
                    <div class="card card-success">
                        <div class="card-header">{{ $t('pages.client-certificate.create.passphrase.title') }}</div>
                        <div class="card-body">
                            <div class="d-flex justify-content-center align-items-center">
                                <div class="text-bold">
                                    {{ passphrase }}
                                </div>
                                <div class="mx-1">
                                    <button class="btn btn-secondary btn-sm" @click="copyToClipboard">
                                        {{ $t('pages.client-certificate.create.copy') }}
                                    </button>
                                </div>
                            </div>
                            <div class="mt-2 d-flex justify-content-center align-items-center">
                                <p>{{ $t('pages.client-certificate.create.passphrase.hint') }}</p>
                            </div>

                        </div>
                    </div>
                </div>

                <div id="notifications" v-for="notification in notifications">
                    <div class="info-box" :class="`bg-${notification.cssClass}`">
                        <span class="info-box-icon"><i class="fas" :class="`fa-${notification.cssFlag}`"></i></span>
                        <div class="info-box-content">
                            <span class="info-box-text">{{ notification.simpleMessage }}</span>
                        </div>
                    </div>
                </div>

                <div id="terminally_failed" v-if="terminally_failed">
                    <div class="card card-danger">
                        <div class="card-header">Fehlschlag</div>
                        <div class="card-body">
                            <p v-if="status_report">
                                {{ status_report }}
                            </p>
                            <p v-else>
                                Fehler im Antrag.
                            </p>
                        </div>
                    </div>
                </div>

                <div id="finally_successful" v-if="finally_successful">
                    <div class="card card-success">
                        <div class="card-header">Erfolgreich</div>
                        <div class="card-body">
                            <p>{{ status_report }}</p>
                        </div>
                    </div>
                </div>

            </div>
        </b-card>

    </div>
</template>

<script>
import GenericNotification from "../layout/GenericNotification";
import Callout from "../layout/Callout";
import PageCard from "../layout/PageCard";
import LoadingInjector from "../../mixins/LoadingInjector";
import purify from "dompurify";

export default {
    mixins: [
        LoadingInjector
    ],
    components: {
        PageCard,
        Callout,
        GenericNotification,
    },
    props: {
        rzaccount: {
            required: true,
            type: String,
        }
    },

    data() {
        return {
            notifications: [],
            current_password: '',
            passphrase: '',
            ccid: null,
            requested: false,
            inner_loading_indicator: false,
            terminally_failed: false,
            finally_successful: false,
            status_report: '',
        }
    },
    created() {
        this.errorHandler = this.$shared.errorHandlerForResponder;
    },
    mounted() {
        console.log('Mounted ClientCertificateNew.');
        this.$Echo.private(`rz-account.${this.rzaccount}`)
            .listen('BolognaEvent', (e) => {
                this.notifications.push(e);
                console.log(e.simpleMessage)

                if (e.name === 'ClientCertificateDownloaded' && e.cssClass === 'success') {
                    this.inner_loading_indicator = false;
                    this.finally_successful = true;
                }

                if (e.name === 'ClientCertificateProcessFailure') {
                    this.inner_loading_indicator = false;
                    this.terminally_failed = true;
                }
            });
    },
    methods: {
        requestCertificate: function () {
            this.requested = true;
            this.inner_loading_indicator = true;
            this.$http.post(this.$Bologna.secure_url(`/v1/client-certificate/create/${this.rzaccount}`), {
                'current_password': this.current_password
            })
                .then(response => {
                    this.passphrase = purify.sanitize(response.data.data.passphrase);
                    this.ccid = purify.sanitize(response.data.data.ccid);
                })
                .catch(error => {
                    console.log(error);
                    this.requested = false;
                })
                .finally(() => {

                });
        },
        copyToClipboard: function () {
            navigator.clipboard.writeText(this.passphrase);
            alert(this.$t('pages.client-certificate.create.copied'))
        },
        statusReport: function () {
            this.$http.get(this.$Bologna.secure_url(`/v1/client-certificate/status/${this.rzaccount}/${this.ccid}`))
                .then(response => {
                    console.log(response.data);
                    this.status_report = purify.sanitize(response.data.data.message);
                })
                .catch(error => {
                })
                .finally(() => {

                });
        }
    },
    computed: {
        passwordEntered: function () {
            return this.current_password.length > 8;
        },
        hasPassphrase: function () {
            return this.passphrase.length > 0;
        },
        hasCcid: function () {
            return this.ccid !== null && this.ccid.length > 0;
        }
    },
    watch: {
        terminally_failed: function (v) {
            if (v === true) {
                this.statusReport();
            }
        },
        finally_successful: function (v) {
            if (v === true) {
                this.statusReport();
            }
        }
    }
};
</script>
